import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { ArrowLeft, Share2, Bookmark, Calendar, Clock } from 'lucide-react';

interface StoryPageProps {
  slug: string;
}

interface Story {
  headline: string;
  deck: string;
  byline: string;
  publishDate: string;
  thumbnail: string;
  category: string;
  readTime: string;
  tags: string[];
  editorNote: string;
  sources: Array<{
    id: number;
    text: string;
    url: string;
  }>;
  breaking?: boolean;
}

export default function StoryPage({ slug }: StoryPageProps) {
  const [story, setStory] = useState<Story | null>(null);
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load story data and embed content directly
    import('@/data/story.json').then(module => {
      setStory(module.default);
      
      // Embed the story content directly for now
      const storyContent = `Violent crime has fallen nationally for two straight years, yet the nightly reel still points at the same ZIP codes. Look at the data and you'll find places like Springfield, Missouri and Billings, Montana — overwhelmingly white, yet posting violent-crime rates that rival or exceed heavily covered urban hotspots. The crimes are real. The silence is deliberate.

## The Data We Ignore

According to the FBI's Uniform Crime Reporting (UCR) program and state crime reports, violent crime in the U.S. dropped about 3% in 2023 and another 4.5% in 2024. The national violent-crime rate in 2023 was 364 per 100,000 residents.

But in majority-white states and cities, the numbers often run much higher:

- **Alaska (statewide):** 726 per 100k
- **Tennessee (statewide):** ~628 per 100k
- **Arkansas (statewide):** ~620 per 100k
- **Missouri (statewide):** 459 per 100k
- **Montana (statewide):** 442 per 100k
- **Springfield, MO (city):** 1,178 per 100k
- **Billings, MT (city):** ~929 per 100k
- **Rockford, IL (city):** ~1,148 per 100k
- **Davenport, IA (city):** ~691 per 100k
- **Rapid City, SD (city):** ~742 per 100k

Each of these jurisdictions is majority-white by Census counts. Yet they rarely make the evening news when crime is discussed at the national level.

## What the Cameras Choose

Research on media bias consistently shows that crime coverage overrepresents Black suspects and neighborhoods, even when actual arrest data doesn't support that distortion. Meanwhile, majority-white communities with equally high or higher rates of violence are underreported.

The effect is powerful: it shapes public perception, fuels stereotypes, and influences how policy is written and how resources are distributed.

## Why It Matters

When only some neighborhoods are shown as dangerous, everyone loses. Communities are stigmatized. Policy debates are distorted. And victims in under-covered places — whether in Montana, Missouri, or Alaska — do not get the same visibility or urgency.

Crime is a national issue, not a racial one. By shining a light on overlooked communities, we're not excusing crime anywhere — we're demanding an honest, balanced accounting everywhere.

*This investigation is based on FBI Uniform Crime Reporting data, Bureau of Justice Statistics, and comprehensive analysis of media coverage patterns. Sources available upon request.*`;
      
      setContent(storyContent);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Story Not Found</h1>
          <Link href="/bmz" className="text-red-600 hover:text-red-700">
            ← Back to BMZ
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <div className="border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link href="/bmz" className="inline-flex items-center text-red-600 hover:text-red-700 font-medium">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to BMZ
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 via-black to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-12">
          {/* Breaking Badge */}
          {story.breaking && (
            <div className="mb-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold text-white bg-red-700 animate-pulse">
                BREAKING NEWS
              </span>
            </div>
          )}

          {/* Category */}
          <div className="text-red-300 text-sm font-medium uppercase tracking-wide mb-4">
            {story.category}
          </div>

          {/* Headline */}
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            {story.headline}
          </h1>

          {/* Deck */}
          <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl">
            {story.deck}
          </p>

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-300">
            <div className="flex items-center">
              <span className="font-medium">By {story.byline}</span>
            </div>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              {formatDate(story.publishDate)}
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              {story.readTime}
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Editor's Note */}
        {story.editorNote && (
          <div className="bg-gray-50 border-l-4 border-red-600 p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-2">Editor's Note</h3>
            <p className="text-gray-700 text-sm">{story.editorNote}</p>
          </div>
        )}

        {/* Article Body */}
        <div className="prose prose-lg max-w-none">
          <div 
            className="story-content"
            dangerouslySetInnerHTML={{ 
              __html: content.replace(/\n/g, '<br>').replace(/## /g, '<h2>').replace(/### /g, '<h3>') 
            }} 
          />
        </div>

        {/* Tags */}
        {story.tags && story.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Related Topics</h3>
            <div className="flex flex-wrap gap-2">
              {story.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Sources */}
        {story.sources && story.sources.length > 0 && (
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Sources</h3>
            <ol className="space-y-2">
              {story.sources.map((source) => (
                <li key={source.id} className="text-sm">
                  <span className="text-gray-600">{source.id}. </span>
                  <a 
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-700 underline"
                  >
                    {source.text}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Share Section */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Share This Story</h3>
              <p className="text-sm text-gray-600">Help spread awareness about this important issue</p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </button>
              <button className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                <Bookmark className="w-4 h-4 mr-2" />
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}